import {create} from 'zustand';
import {DashboardViewMode} from '../types/viewMode.types';

interface DashboardUIState {
  // View Mode - can be single mode or 'both' to show stores and marketers together
  viewMode: DashboardViewMode;
  setViewMode: (mode: DashboardViewMode) => void;

  // Separate flags for showing stores and marketers (for toggle behavior)
  showStores: boolean;
  showMarketers: boolean;
  setShowStores: (show: boolean) => void;
  setShowMarketers: (show: boolean) => void;
  toggleStores: () => void;
  toggleMarketers: () => void;

  // Sidebar State
  isCollapsed: boolean;
  isMobile: boolean;
  isMobileMenuOpen: boolean;

  // Sidebar Actions
  setIsCollapsed: (collapsed: boolean) => void;
  setIsMobile: (mobile: boolean) => void;
  setIsMobileMenuOpen: (open: boolean) => void;
  toggleSidebar: () => void;
  closeMobileMenu: () => void;

  // Utility Actions
  reset: () => void;
}

/**
 * Helper function to calculate viewMode based on visibility flags
 */
const calculateViewMode = (
  showStores: boolean,
  showMarketers: boolean
): DashboardViewMode => {
  if (showStores && showMarketers) {
    return 'both';
  }
  if (showStores) {
    return 'stores';
  }
  if (showMarketers) {
    return 'marketers';
  }
  // Default to stores if both are false
  return 'stores';
};

export const useDashboardUIStore = create<DashboardUIState>((set, get) => ({
  // View Mode
  viewMode: 'stores',
  setViewMode: (mode) => {
    // When setting viewMode, update show flags accordingly
    if (mode === 'both') {
      set({viewMode: 'both', showStores: true, showMarketers: true});
    } else if (mode === 'stores') {
      set({viewMode: 'stores', showStores: true, showMarketers: false});
    } else if (mode === 'marketers') {
      set({viewMode: 'marketers', showStores: false, showMarketers: true});
    } else {
      set({viewMode: mode});
    }
  },

  // Separate flags for toggle behavior
  showStores: true,
  showMarketers: false,
  setShowStores: (show) => {
    const state = get();
    const newShowStores = show;
    const newShowMarketers = state.showMarketers;

    // Prevent both flags from being false
    if (!newShowStores && !newShowMarketers) {
      set({showStores: true, viewMode: 'stores'});
      return;
    }

    const newViewMode = calculateViewMode(newShowStores, newShowMarketers);
    set({showStores: newShowStores, viewMode: newViewMode});
  },
  setShowMarketers: (show) => {
    const state = get();
    const newShowStores = state.showStores;
    const newShowMarketers = show;

    // Prevent both flags from being false
    if (!newShowStores && !newShowMarketers) {
      set({showMarketers: false, showStores: true, viewMode: 'stores'});
      return;
    }

    const newViewMode = calculateViewMode(newShowStores, newShowMarketers);
    set({showMarketers: newShowMarketers, viewMode: newViewMode});
  },
  toggleStores: () => {
    const state = get();
    state.setShowStores(!state.showStores);
  },
  toggleMarketers: () => {
    const state = get();
    state.setShowMarketers(!state.showMarketers);
  },

  // Sidebar State
  isCollapsed: false,
  isMobile: false,
  isMobileMenuOpen: false,

  // Sidebar Actions
  setIsCollapsed: (collapsed) => set({isCollapsed: collapsed}),
  setIsMobile: (mobile) => set({isMobile: mobile}),
  setIsMobileMenuOpen: (open) => set({isMobileMenuOpen: open}),

  toggleSidebar: () => {
    const {isMobile, isCollapsed, isMobileMenuOpen} = get();
    if (isMobile) {
      set({isMobileMenuOpen: !isMobileMenuOpen});
    } else {
      set({isCollapsed: !isCollapsed});
    }
  },

  closeMobileMenu: () => set({isMobileMenuOpen: false}),

  // Utility Actions
  reset: () => set({
    viewMode: 'stores',
    showStores: true,
    showMarketers: false,
    isCollapsed: false,
    isMobile: false,
    isMobileMenuOpen: false
  })
}));
